import type { WebContainer } from '@webcontainer/api';
import { atom } from 'nanostores';
import { persistUserFile } from '../runtime/tool-adapters/writeFile';
import { getMergedFileContent } from '../utils/mergedFiles';

/**
 * Undo and redo for hot edits only (hot edit plan, phase 2). Each hot-edit write records the
 * file's content before and after; undo writes `before` back through the same save path as the
 * edit, redo writes `after`. The model's changes from the chat are not undone here: the stacks
 * are cleared when a chat turn starts, and an entry whose file changed since is refused.
 */
export type HotEditHistoryEntry = { filePath: string; before: string; after: string };
export type HotEditHistory = { undo: HotEditHistoryEntry[]; redo: HotEditHistoryEntry[] };

export const HOT_EDIT_HISTORY_LIMIT = 50;

export const hotEditHistory = atom<HotEditHistory>({ undo: [], redo: [] });

/** The stacks after a new edit: pushed onto undo (capped), redo cleared. */
export function recordEdit(history: HotEditHistory, entry: HotEditHistoryEntry): HotEditHistory {
  if (entry.before === entry.after) return history;
  return { undo: [...history.undo, entry].slice(-HOT_EDIT_HISTORY_LIMIT), redo: [] };
}

export type HistoryStep =
  | { kind: 'write'; filePath: string; content: string; next: HotEditHistory }
  | { kind: 'empty' }
  | { kind: 'stale'; next: HotEditHistory };

/**
 * The write that undoes (or redoes) the last entry. `stale` when the file no longer holds the
 * content the entry left (the model or the code editor changed it since): the entry is dropped
 * instead of overwriting that change.
 */
export function historyStep(
  history: HotEditHistory,
  direction: 'undo' | 'redo',
  currentContent: (filePath: string) => string | null,
): HistoryStep {
  const from = direction === 'undo' ? history.undo : history.redo;
  const entry = from[from.length - 1];
  if (!entry) return { kind: 'empty' };
  const rest = from.slice(0, -1);
  const expected = direction === 'undo' ? entry.after : entry.before;
  if (currentContent(entry.filePath) !== expected) {
    return { kind: 'stale', next: direction === 'undo' ? { ...history, undo: rest } : { ...history, redo: rest } };
  }
  const content = direction === 'undo' ? entry.before : entry.after;
  const next =
    direction === 'undo'
      ? { undo: rest, redo: [...history.redo, entry] }
      : { undo: [...history.undo, entry], redo: rest };
  return { kind: 'write', filePath: entry.filePath, content, next };
}

/** Writes a hot edit and records it for undo. */
export async function writeHotEdit(wc: WebContainer | null, filePath: string, content: string): Promise<void> {
  const before = getMergedFileContent(filePath) ?? '';
  await persistUserFile(wc, filePath, content, true);
  hotEditHistory.set(recordEdit(hotEditHistory.get(), { filePath, before, after: content }));
}

/** Undoes or redoes the last hot edit. Returns false when there was nothing to do. */
export async function stepHotEditHistory(wc: WebContainer | null, direction: 'undo' | 'redo'): Promise<boolean> {
  const step = historyStep(hotEditHistory.get(), direction, (p) => getMergedFileContent(p));
  if (step.kind === 'empty') return false;
  hotEditHistory.set(step.next);
  if (step.kind === 'stale') return false;
  await persistUserFile(wc, step.filePath, step.content, true);
  return true;
}

export function clearHotEditHistory(): void {
  hotEditHistory.set({ undo: [], redo: [] });
}
